// PreflightView.tsx — SQA-26
// /contacts/:id/preflight — on-demand Project Brief Preflight viewer.
// INTERNAL ONLY — never shown to customers.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  ProjectBriefPreflight,
  PreflightStatus,
  ConfirmedScopeItem,
  UnresolvedScopeItem,
  CommitmentItem,
  PrebuildQuestion,
  TimelineMilestone,
  RiskItem,
} from '../../../src/preflight/types';

// ── status chip ───────────────────────────────────────────────────────────

const STATUS_CHIP: Record<PreflightStatus, string> = {
  usable: 'bg-green-50 text-green-900 ring-green-200',
  needs_review: 'bg-amber-50 text-amber-900 ring-amber-200',
  insufficient_evidence: 'bg-slate-100 text-slate-600 ring-slate-200',
  stale_corpus: 'bg-orange-50 text-orange-900 ring-orange-200',
};

const STATUS_LABEL: Record<PreflightStatus, string> = {
  usable: 'Usable',
  needs_review: 'Needs Review',
  insufficient_evidence: 'Insufficient Evidence',
  stale_corpus: 'Stale Corpus',
};

const COMMITMENT_CHIP: Record<ConfirmedScopeItem['commitment_level'], string> = {
  firm: 'bg-green-50 text-green-800 ring-green-200',
  soft: 'bg-yellow-50 text-yellow-800 ring-yellow-200',
  inferred: 'bg-slate-50 text-slate-700 ring-slate-200',
};

const PRIORITY_CHIP: Record<PrebuildQuestion['priority'], string> = {
  blocking: 'bg-rose-50 text-rose-800 ring-rose-200',
  high: 'bg-amber-50 text-amber-800 ring-amber-200',
  normal: 'bg-sky-50 text-sky-800 ring-sky-200',
};

const SEVERITY_CHIP: Record<RiskItem['severity'], string> = {
  high: 'bg-rose-50 text-rose-800 ring-rose-200',
  medium: 'bg-amber-50 text-amber-800 ring-amber-200',
  low: 'bg-slate-50 text-slate-600 ring-slate-200',
};

// ── helpers ───────────────────────────────────────────────────────────────

function Chip({
  label,
  className,
}: {
  label: string;
  className: string;
}): JSX.Element {
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${className}`}>
      {label}
    </span>
  );
}

function Section({
  title,
  children,
  empty,
}: {
  title: string;
  children: React.ReactNode;
  empty?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg bg-white ring-1 ring-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="px-4 py-3">
        {children ?? <p className="text-xs text-slate-400 italic">{empty ?? 'None'}</p>}
      </div>
    </div>
  );
}

function ConfidenceBar({ value }: { value: number }): JSX.Element {
  const pct = Math.round(value * 100);
  const color = value >= 0.7 ? 'bg-green-500' : value >= 0.4 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500 tabular-nums">{pct}%</span>
    </div>
  );
}

// ── section renderers ─────────────────────────────────────────────────────

function ConfirmedScopeSection({ items }: { items: ConfirmedScopeItem[] }): JSX.Element {
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 italic">No confirmed scope items</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <Chip label={item.commitment_level} className={COMMITMENT_CHIP[item.commitment_level]} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-800">{item.item}</p>
            {item.committed_by && (
              <p className="text-xs text-slate-500 mt-0.5">by {item.committed_by}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function UnresolvedScopeSection({ items }: { items: UnresolvedScopeItem[] }): JSX.Element {
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 italic">No unresolved scope items</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="border-l-2 border-amber-300 pl-3">
          <p className="text-sm text-slate-800">{item.item}</p>
          <p className="text-xs text-amber-700 mt-0.5">{item.clarification_needed}</p>
        </li>
      ))}
    </ul>
  );
}

function CommitmentsSection({ items, label }: { items: CommitmentItem[]; label: string }): JSX.Element {
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 italic">No {label.toLowerCase()} recorded</p>;
  }
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-0.5 text-slate-400 text-xs tabular-nums">{Math.round(item.confidence * 100)}%</span>
          <p className="text-sm text-slate-800 flex-1">{item.commitment}</p>
        </li>
      ))}
    </ul>
  );
}

function QuestionsSection({ items }: { items: PrebuildQuestion[] }): JSX.Element {
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 italic">No open questions</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <Chip label={item.priority} className={PRIORITY_CHIP[item.priority]} />
          <p className="text-sm text-slate-800 flex-1">{item.question}</p>
        </li>
      ))}
    </ul>
  );
}

function TimelineSection({ items }: { items: TimelineMilestone[] }): JSX.Element {
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 italic">No timeline information found</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="text-xs font-medium text-slate-500 tabular-nums w-24 shrink-0 mt-0.5">
            {item.target_date ?? 'No date'}
          </span>
          <div className="flex-1">
            <p className="text-sm text-slate-800">{item.milestone}</p>
            {item.notes && <p className="text-xs text-slate-500 mt-0.5">{item.notes}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function RisksSection({ items }: { items: RiskItem[] }): JSX.Element {
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 italic">No risks identified</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <Chip label={item.severity} className={SEVERITY_CHIP[item.severity]} />
          <p className="text-sm text-slate-800 flex-1">{item.risk}</p>
        </li>
      ))}
    </ul>
  );
}

// ── main view ─────────────────────────────────────────────────────────────

export function PreflightView(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [preflight, setPreflight] = useState<ProjectBriefPreflight | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectThreadId, setProjectThreadId] = useState('');

  async function generate(): Promise<void> {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPreflight(null);
    try {
      const body: Record<string, string> = {};
      if (projectThreadId.trim()) body.project_thread_id = projectThreadId.trim();
      const res = await fetch(`/api/contacts/${id}/project-brief-preflight`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({ error: `HTTP ${res.status}` }))) as { error?: string };
        setError(err.error ?? `HTTP ${res.status}`);
        return;
      }
      const data = (await res.json()) as ProjectBriefPreflight;
      setPreflight(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* back link */}
      <Link
        to={`/contacts/${id}`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        ← Contact detail
      </Link>

      {/* header */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Project Brief Preflight</h2>
        <p className="mt-0.5 text-xs text-amber-700 font-medium">
          ⚠ Internal use only — not a final production packet
        </p>
      </div>

      {/* controls */}
      <div className="rounded-lg bg-white ring-1 ring-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-48">
          <label className="block text-xs font-medium text-slate-700 mb-1">
            Project Thread ID <span className="text-slate-400">(optional)</span>
          </label>
          <input
            type="text"
            value={projectThreadId}
            onChange={(e) => setProjectThreadId(e.target.value)}
            placeholder="e.g. PT-2026-001"
            className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>
        <button
          type="button"
          onClick={() => { void generate(); }}
          disabled={loading}
          className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 transition"
        >
          {loading ? 'Generating…' : 'Generate Preflight'}
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      {preflight && (
        <div className="space-y-4">
          {/* status header */}
          <div className="rounded-lg bg-white ring-1 ring-slate-200 p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">
                  {preflight.disclaimer}
                </p>
                <p className="text-lg font-semibold text-slate-900">{preflight.identity.display_name}</p>
                <p className="text-sm text-slate-500">
                  {preflight.identity.company ?? preflight.identity.primary_phone ?? preflight.identity.primary_email}
                </p>
              </div>
              <Chip
                label={STATUS_LABEL[preflight.preflight_status]}
                className={STATUS_CHIP[preflight.preflight_status]}
              />
            </div>
            <div className="mt-3">
              <p className="text-xs text-slate-500 mb-1">Evidence confidence</p>
              <ConfidenceBar value={preflight.overall_confidence} />
            </div>
            {preflight.warnings.length > 0 && (
              <ul className="mt-3 space-y-1">
                {preflight.warnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-700 flex items-start gap-1">
                    <span>⚠</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* vehicle & project */}
          <Section title="Vehicle & Project">
            {preflight.vehicle_and_project.summary ? (
              <div className="space-y-1">
                <p className="text-sm text-slate-800">{preflight.vehicle_and_project.summary}</p>
                {preflight.vehicle_and_project.vehicle_description && (
                  <p className="text-xs text-slate-500">Vehicle: {preflight.vehicle_and_project.vehicle_description}</p>
                )}
                {preflight.vehicle_and_project.build_type && (
                  <p className="text-xs text-slate-500">Build type: {preflight.vehicle_and_project.build_type}</p>
                )}
                {preflight.vehicle_and_project.project_phase && (
                  <p className="text-xs text-slate-500">Phase: {preflight.vehicle_and_project.project_phase}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">VERIFY — no vehicle/project info found</p>
            )}
          </Section>

          {/* confirmed scope */}
          <Section title="Confirmed Scope">
            <ConfirmedScopeSection items={preflight.confirmed_scope} />
          </Section>

          {/* unresolved scope */}
          {preflight.unresolved_scope.length > 0 && (
            <Section title="Unresolved Scope">
              <UnresolvedScopeSection items={preflight.unresolved_scope} />
            </Section>
          )}

          {/* commitments */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Section title="ODB Commitments">
              <CommitmentsSection items={preflight.odb_commitments} label="ODB commitments" />
            </Section>
            <Section title="Customer Commitments">
              <CommitmentsSection items={preflight.customer_commitments} label="customer commitments" />
            </Section>
          </div>

          {/* open pre-build questions */}
          <Section title="Open Pre-Build Questions">
            <QuestionsSection items={preflight.open_prebuild_questions} />
          </Section>

          {/* timeline */}
          {preflight.schedule_and_timeline.length > 0 && (
            <Section title="Schedule & Timeline">
              <TimelineSection items={preflight.schedule_and_timeline} />
            </Section>
          )}

          {/* risks */}
          {preflight.risks_and_blockers.length > 0 && (
            <Section title="Risks & Blockers">
              <RisksSection items={preflight.risks_and_blockers} />
            </Section>
          )}

          {/* suggested next action */}
          <Section title="Suggested Next Action">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-800">{preflight.suggested_next_action.action}</p>
              <p className="text-xs text-slate-600">{preflight.suggested_next_action.rationale}</p>
              <div className="mt-2">
                <ConfidenceBar value={preflight.suggested_next_action.confidence} />
              </div>
            </div>
          </Section>

          {/* what not to promise */}
          {preflight.what_not_to_promise.length > 0 && (
            <Section title="What Not to Promise">
              <ul className="space-y-2">
                {preflight.what_not_to_promise.map((item, i) => (
                  <li key={i} className="border-l-2 border-rose-300 pl-3">
                    <p className="text-sm font-medium text-slate-800">{item.item}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{item.reason}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* citations */}
          {preflight.citations.length > 0 && (
            <Section title={`Citations (${preflight.citations.length})`}>
              <ul className="space-y-2">
                {preflight.citations.map((c, i) => (
                  <li key={i} className="text-xs">
                    <span className="font-mono text-slate-500">{c.source_ref}</span>
                    {c.title && <span className="ml-2 text-slate-700">{c.title}</span>}
                    {c.occurred_at && (
                      <span className="ml-2 text-slate-400">{new Date(c.occurred_at).toLocaleDateString()}</span>
                    )}
                    <p className="text-slate-500 mt-0.5 line-clamp-2">{c.excerpt}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
